<template>
  <section
    ref="revealTarget"
    class="section organizations-section home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="page-container">
      <div class="section-heading-row">
        <div class="section-head">
          <span class="section-kicker">PARTY & UNION</span>
          <h2>组织建设与职工行动</h2>
        </div>
        <nuxt-link v-if="partyPath" class="section-more" :to="partyPath">
          查看更多
          <i class="i-custom-enter more-link-icon-desktop" aria-hidden="true"></i>
          <i class="i-lucide-chevron-right more-link-icon-mobile" aria-hidden="true"></i>
        </nuxt-link>
      </div>

      <div class="organization-grid">
        <nuxt-link
          v-for="item in organizations"
          :key="item.title"
          class="organization-card"
          :to="item.to"
        >
          <div class="organization-media">
            <home-media :source="item.image" :alt="item.title" loading="lazy" />
          </div>
          <div class="organization-overlay"></div>
          <div class="organization-copy">
            <h3>{{ item.title }}</h3>
            <p>{{ item.description }}</p>
            <span>
              了解栏目规划
              <i class="i-custom-enter" aria-hidden="true"></i>
            </span>
          </div>
        </nuxt-link>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { getArticleCategoryEntryRoute } from '~/utils/content'

  defineOptions({
    name: 'HomeOrganizations',
  })

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const { data: categoryResponse } = useArticleCategories()
  const partyPath = computed(() => {
    const root = categoryResponse.value?.data.find((category) => category.code.includes('party'))
    return root ? getArticleCategoryEntryRoute(root).path : undefined
  })
  const unionPath = computed(() => {
    const root = categoryResponse.value?.data.find((category) => category.code.includes('union'))
    return root ? getArticleCategoryEntryRoute(root).path : undefined
  })

  const organizations = computed(() => [
    {
      title: '兴科党建',
      description: '预留党建动态、专题学习、党建活动与先锋故事的官方发布入口。',
      image: '/images/company/strength/pilot-base.jpg',
      to: partyPath.value ?? undefined,
    },
    {
      title: '兴科工会',
      description: '记录送温暖、劳模先锋、职工风采与关怀活动等真实企业内容。',
      image: '/images/company/staff-visitor-cafe.webp',
      to: unionPath.value ?? undefined,
    },
  ])
</script>

<style scoped lang="scss">
  .organizations-section {
    background: var(--surface-soft);
  }

  .section-heading-row {
    display: flex;
    gap: 24px;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 48px;
  }

  .section-head {
    max-width: 780px;
    margin-bottom: 0;
  }

  .section-head h2 {
    margin: 0;
    font-size: clamp(28px, 3.2vw, 44px);
    font-weight: 620;
    line-height: 1.18;
  }

  .section-kicker {
    display: inline-flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
    font:
      650 12px/1.4 ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    color: var(--accent);
  }

  .section-kicker::before {
    width: 28px;
    height: 2px;
    content: '';
    background: currentColor;
  }

  .section-more {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 8px;
    align-items: center;
    min-height: 44px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--more);
    white-space: nowrap;
    cursor: pointer;
  }

  .section-more i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  .section-more:hover {
    color: var(--brand-strong);
  }

  .section-more:hover i,
  .organization-card:hover .organization-copy i {
    transform: translateX(4px);
  }

  .organization-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px;
  }

  .organization-card {
    position: relative;
    display: block;
    min-width: 0;
    aspect-ratio: 1.86 / 1;
    overflow: hidden;
    color: #fff;
    background: var(--surface-soft);
    border-radius: var(--radius-md);
    isolation: isolate;
  }

  .organization-media {
    position: absolute;
    inset: 0;
  }

  .organization-media :deep(.home-media) {
    transition: transform var(--normal) var(--ease);
  }

  .organization-card:hover .organization-media :deep(.home-media) {
    transform: scale(1.035);
  }

  .organization-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    content: '';
    background: linear-gradient(180deg, rgb(12 16 20 / 0%) 28%, rgb(12 16 20 / 78%) 100%);
  }

  .organization-copy {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
    padding: 32px 28px 28px;
  }

  .organization-copy h3 {
    margin: 0;
    font-size: 28px;
    font-weight: 620;
    line-height: 1.3;
  }

  .organization-copy p {
    max-width: 70ch;
    margin: 14px 0 0;
    font-size: 16px;
    line-height: 1.6;
    color: rgb(255 255 255 / 82%);
  }

  .organization-copy span {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    margin-top: 18px;
    font-size: 15px;
    line-height: 1.5;
    color: #fff;
  }

  .organization-copy i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  @media (max-width: 767px) {
    .section-heading-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 14px 16px;
      align-items: center;
      margin-bottom: 36px;
    }

    .section-head {
      display: contents;
    }

    .section-kicker {
      grid-row: 1;
      grid-column: 1;
      min-width: 0;
      margin-bottom: 0;
    }

    .section-head h2 {
      grid-row: 2;
      grid-column: 1 / -1;
    }

    .section-more {
      grid-row: 1;
      grid-column: 2;
      align-self: center;
      margin: 0;
    }

    .organization-grid {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .organization-card {
      aspect-ratio: 1.65 / 1;
    }

    .organization-copy {
      padding: 24px 20px 20px;
    }

    .organization-copy h3 {
      font-size: 22px;
    }

    .organization-copy p {
      margin-top: 10px;
      font-size: 14px;
    }

    .organization-copy span {
      margin-top: 14px;
      font-size: 14px;
    }
  }
</style>
